/**
 * The idea of Behaviors is to keep the static implementation of a UI widget functionality separate so that we can plug it anytime with any widget
 */
import WidgetStates from './hooks/WidgetStates';
import getStorageAPIValue from './hooks/getStorageAPIValue';
import hideElement from './hooks/hideElement';
import observe from './hooks/observe';
import showElement from './hooks/showElement';
import storageAPIExists from './hooks/storageAPIExists';
import WidgetTypes from './enums/WidgetTypes';

const Behaviors = {
    NOTIFICATIONS: {
        setup: false, // a flag to keep track of setupNotification to run only once
        setupNotification() {
            const me = this;
            if (me.setup) {
                return;
            }
            me.setup = true;
            me.bubbleContainer = me.DOM.getElementById('apexchat_rd_invitation_bubble');
            me.notificationContainer = me.DOM.getElementById('apexchat_rd_invitation_typing');
            me.typingIndicatorAnimatedDots = me.DOM.getElementsByClassName('apexchat_rd_glider');
            me.typingIndicatorAnimatedDotsContainer = me.DOM.getElementsByClassName(
                'apexchat_rd_invitation_animation'
            );
            me.typingIndicator = me.DOM.getElementsByClassName(
                'apexchat_rd_invitation_typing_dots'
            );
            me.messageCountContainer = me.DOM.getElementById('apexchat_rd_notification_count');
        },
        showNotification() {
            const me = this;
            me.setupNotification();
            if (me.notificationContainer) {
                hideElement(me.notificationContainer);
                showElement(me.messageCountContainer);
                showElement(me.bubbleContainer);
                me.messageCountContainer.innerHTML = me.newMsgNotificationsCount;
            }
        },
        resetNotificationCount() {
            const me = this;
            if (me.notificationContainer) {
                hideElement(me.messageCountContainer);
                hideElement(me.notificationContainer);
                hideElement(me.bubbleContainer);
                ApexChat.storageAPI.Remove('livechat_new_msg_count');
                me.messageCountContainer.innerHTML = '';
                me.newMsgNotificationsCount = 0;
            }
        },
        showTypingInMinimizedState() {
            const me = this;
            me.setupNotification(); // Re-setup if page refreshes then old messages will come then first of all typing indicators come.
            if (me.notificationContainer) {
                showElement(me.notificationContainer);
                showElement(me.bubbleContainer);
                for (let i = 0; i < me.typingIndicator.length; i++) {
                    showElement(me.typingIndicator[i]);
                }
                me.DOM.querySelector('.apexchat_rd_invitation_typing')?.classList.remove('disabled');
                me.DOM.querySelector('.apexchat_rd_invitation_bubble')?.classList.remove('disabled');
            }
        },
        hideTypingInMinimizedState() {
            const me = this;
            if (me.notificationContainer) {
                for (let i = 0; i < me.typingIndicator.length; i++) {
                    hideElement(me.typingIndicator[i]);
                }
            }
            // hideElement(me.bubbleContainer);
            // hideElement(me.notificationContainer);
            me.DOM.querySelector('.apexchat_rd_invitation_typing')?.classList.add('disabled');
            me.DOM.querySelector('.apexchat_rd_invitation_bubble')?.classList.add('disabled');
        },
        showTypingWithAnimatedDotsInMinimizedState() {
            const me = this;
            if (me.typingIndicatorAnimatedDots) {
                for (let i = 0; i < me.typingIndicatorAnimatedDots.length; i++) {
                    showElement(me.typingIndicatorAnimatedDots[i]);
                }
            }
            me.showTypingWithAnimatedDotsContainer();
        },
        hideTypingWithAnimatedDotsInMinimizedState() {
            const me = this;
            if (me.typingIndicatorAnimatedDots) {
                for (let i = 0; i < me.typingIndicatorAnimatedDots.length; i++) {
                    hideElement(me.typingIndicatorAnimatedDots[i]);
                }
            }
        },
        showTypingWithAnimatedDotsContainer() {
            const me = this;
            if (me.typingIndicatorAnimatedDotsContainer) {
                showElement(me.typingIndicatorAnimatedDotsContainer[0]);
            }
        },
        hideTypingWithAnimatedDotsConatiner() {
            const me = this;
            if (me.typingIndicatorAnimatedDotsContainer) {
                hideElement(me.typingIndicatorAnimatedDotsContainer[0]);
            }
        },
        hideMsgCounts() {
            const me = this;
            if (me.messageCountContainer) {
                hideElement(me.messageCountContainer);
            }
        },
        showMsgCounts() {
            const me = this;
            if (me.messageCountContainer) {
                if (me.messageCountContainer.innerHTML != '') {
                    showElement(me.messageCountContainer);
                    showElement(me.bubbleContainer);
                } else {
                    // hideElement(me.notificationContainer);
                }
            }
        },
        increaseNotificationCounter(isChatFromAgent) {
            const me = this;
            const lastDOMState = me.state.getStateValue(WidgetTypes.DOM);
            // we need only agent's msgs to show bubble counter of; and only when the RD/DOM is minimized
            if (
                isChatFromAgent &&
                (lastDOMState == WidgetStates.ShownMinimized ||
                    lastDOMState == WidgetStates.ShownNormal)
            ) {
                me.newMsgNotificationsCount = me.newMsgNotificationsCount || 0;
                me.newMsgNotificationsCount++;
                ApexChat.storageAPI.StoreTemp(
                    'livechat_new_msg_count',
                    me.newMsgNotificationsCount
                );
                ApexChat.storageAPI.StoreTemp('livechat_last_msg_id', isChatFromAgent.m.id);
                ApexChat.storageAPI.StoreTemp('livechat_msg_count_updated', true);
                me.showNotification();
            }
        },
        showNotificationCountOnReload() {
            const me = this;
            if (storageAPIExists('livechat_new_msg_count')) {
                me.newMsgNotificationsCount = getStorageAPIValue('livechat_new_msg_count');
                me.showNotification();
            }
        }
    },
    MAXIMIZE_ON_MINIMIZE: {},
    INVITATION_CLICKABLES: {
        setStartChatHandler(cb) {
            const me = this;
            const clickInviation = (e) => {
                const { language } = me.options;
                const chatInitiatedBy = 'Standard';
                me.onInvitationClick(e, language, chatInitiatedBy);
                if (cb && typeof cb === 'function') {
                    cb();
                }
            };
            observe(
                me.DOM.getElementById('apexchat_rd_agent_image_start_chat'),
                'click',
                clickInviation.bind2(this, 'min')
            );
            observe(
                me.DOM.getElementById('apexchat_rd_notification_count'),
                'click',
                clickInviation.bind2(this, 'min')
            );
            observe(
                me.DOM.getElementById('apexchat_rd_invitation_title'),
                'click',
                clickInviation.bind2(this, 'max')
            );
            observe(
                me.DOM.getElementById('apexchat_greet_video_start_trigger'),
                'click',
                clickInviation.bind2(this, 'max')
            );
            // plug ADA
            me.handleADA(
                me.DOM.getElementById('apexchat_rd_agent_image_start_chat'),
                clickInviation.bind2(this, 'min')
            );
            me.handleADA(
                me.DOM.getElementById('apexchat_rd_notification_count'),
                clickInviation.bind2(this, 'min')
            );
            me.handleADA(
                me.DOM.getElementById('apexchat_rd_invitation_title'),
                clickInviation.bind2(this, 'max')
            );
            me.handleADA(
                me.DOM.getElementById('apexchat_greet_video_start_trigger'),
                clickInviation.bind2(this, 'max')
            );
        }
    }
};
export default Behaviors
